import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useUrlToken } from '@hooks/useUrlToken';
import { AxiosError } from 'axios';
import { useToast } from '@centraoneplus/ui';
import { useAuthStore } from '@stores/authStore';
import { setupService, SetupTokenValidation } from '@services/setup.service';
import { PasswordInput } from '@components/PasswordInput';
import { newPasswordFormSchema, PASSWORD_REQUIREMENTS_MESSAGE } from '@/lib/validation';
import { AuthLinkProblem } from '@components/auth/AuthLinkProblem';

interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

type PageState = 'loading' | 'valid' | 'invalid' | 'expired' | 'error';

export default function SetupAccountPage() {
  const token = useUrlToken();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [tokenData, setTokenData] = useState<SetupTokenValidation | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const toast = useToast();

  useEffect(() => {
    if (!token) {
      setPageState('invalid');
      setError('This invitation link is missing its code. Open the link from your invitation email again, or ask your administrator to send a new invitation.');
      return;
    }

    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const data = await setupService.validateToken(token);
      setTokenData(data);

      if (data.valid) {
        // Pre-fill names if the invite happened to include them; the user can
        // edit. They are collected/required here (not at invitation time).
        setFirstName(data.firstName || '');
        setLastName(data.lastName || '');
        setPageState('valid');
      } else if (data.error === 'Token has expired') {
        setPageState('expired');
        setError('This invitation has expired. Please request a new one.');
      } else {
        setPageState('invalid');
        // The service's own wording ("Invalid setup token") is not something a
        // new user can act on.
        setError('This invitation link is not valid. Ask your administrator to send a new invitation.');
      }
    } catch (err) {
      setPageState('error');
      setError('We could not check this invitation right now. Please try the link again in a few minutes.');
    }
  };

  const validatePassword = (): boolean => {
    // Zod is the single source of truth for the password policy + match check.
    const result = newPasswordFormSchema.safeParse({ password, confirmPassword });
    if (!result.success) {
      setError(result.error.issues[0]?.message ?? PASSWORD_REQUIREMENTS_MESSAGE);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // First/Last name are required at account setup (they are not required at
    // invitation time).
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name.');
      return;
    }

    if (!validatePassword()) {
      return;
    }

    setLoading(true);

    try {
      const response = await setupService.completeSetup({
        token,
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });

      const { user, accessToken } = response;
      login(
        {
          id: user.id,
          email: user.email,
          name: user.name,
          role: user.role,
          tenantId: user.tenantId,
        },
        accessToken
      );

      toast.success('Account created!', `Welcome to ${tokenData?.tenantName || 'CentraOnePlus'}!`);
      navigate('/dashboard');
    } catch (err) {
      const axiosError = err as AxiosError<{ error: ApiError }>;

      if (axiosError.response?.status === 400) {
        const errorCode = axiosError.response.data?.error?.code;
        if (errorCode === 'TOKEN_EXPIRED') {
          setPageState('expired');
          setError('This invitation has expired. Please request a new one.');
        } else if (errorCode === 'TOKEN_ALREADY_USED') {
          setPageState('invalid');
          setError('This invitation has already been used. Please login instead.');
        } else {
          setError(axiosError.response.data?.error?.message || 'Setup failed');
        }
      } else if (axiosError.response?.data?.error?.message) {
        setError(axiosError.response.data.error.message);
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (pageState === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Validating your invitation...</p>
        </div>
      </div>
    );
  }

  // Invalid/expired/error state
  if (pageState !== 'valid') {
    // "Contact Support" used to link to /support, which is not a route: it
    // landed on the 404 page. The message now says who to ask instead.
    return (
      <AuthLinkProblem
        title={
          pageState === 'expired'
            ? 'This invitation has expired'
            : pageState === 'error'
              ? 'This invitation could not be checked'
              : "This invitation link doesn't work"
        }
        message={error}
      />
    );
  }

  // Valid token - show setup form
  return (
    <div>
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Welcome{tokenData?.firstName ? `, ${tokenData.firstName}` : ''}!
        </h2>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Complete your account setup for{' '}
          <span className="font-medium text-gray-900 dark:text-white">
            {tokenData?.tenantName || 'your organization'}
          </span>
        </p>
      </div>

      <form className="space-y-6" onSubmit={handleSubmit}>
        {error && (
          <div className="px-4 py-3 rounded bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {/* Display email (read-only) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Email address
          </label>
          <input
            type="email"
            value={tokenData?.email || ''}
            disabled
            className="input mt-1 bg-gray-50 dark:bg-gray-700 cursor-not-allowed dark:text-gray-300"
          />
        </div>

        {/* Display role */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Your role
          </label>
          <div className="mt-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-md border border-gray-300 dark:border-gray-600">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 capitalize">
              {tokenData?.role || 'User'}
            </span>
          </div>
        </div>

        {/* Name fields — collected here at account setup, not at invitation */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              First name
            </label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="input mt-1 dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
              placeholder="John"
            />
          </div>
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Last name
            </label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="input mt-1 dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
              placeholder="Doe"
            />
          </div>
        </div>

        {/* Password fields */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Create password
          </label>
          <PasswordInput
            id="password"
            name="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
            wrapperClassName="mt-1"
            placeholder="Enter a strong password"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            At least 12 characters with uppercase, lowercase, number, and special character
          </p>
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Confirm password
          </label>
          <PasswordInput
            id="confirmPassword"
            name="confirmPassword"
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="input dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
            wrapperClassName="mt-1"
            placeholder="Confirm your password"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Setting up your account...' : 'Complete Setup'}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
        Already have an account?{' '}
        <Link to="/login" className="text-primary-600 dark:text-primary-400 hover:text-primary-500 font-medium">
          Sign in
        </Link>
      </p>
    </div>
  );
}
